import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ServiceCard from './components/ServiceCard';
import CodeDemo from './components/CodeDemo';
import ProjectCalculator from './components/ProjectCalculator';
import ServiceEcosystem from './components/ServiceEcosystem';
import ProcessTimeline from './components/ProcessTimeline';

const ServicesDeepDive = () => {
  const [expandedService, setExpandedService] = useState(null);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [selectedCalculatorService, setSelectedCalculatorService] = useState(null);
  const [activeView, setActiveView] = useState('ecosystem');

  const services = [
    {
      id: 'custom-dev',
      title: 'Custom Software Development',
      subtitle: 'Tailored Solutions for Unique Challenges',
      icon: 'Code',
      bgColor: 'bg-secondary',
      description: 'End-to-end custom software development that transforms your business vision into powerful, scalable applications. From concept to deployment, we architect solutions that grow with your business.',
      technologies: ['React', 'Node.js', 'Python', 'PostgreSQL', 'AWS', 'Docker'],
      timeline: '8-16 weeks',
      startingPrice: '$25,000',
      methodology: [
        { phase: 'Discovery', description: 'Requirements analysis and planning' },
        { phase: 'Architecture', description: 'System design and technology selection' },
        { phase: 'Development', description: 'Agile development with weekly demos' },
        { phase: 'Deployment', description: 'Production launch and monitoring' }
      ],
      metrics: [
        { value: '40%', label: 'Faster Time-to-Market' },
        { value: '99.9%', label: 'Uptime Guarantee' },
        { value: '60%', label: 'Cost Reduction vs In-house' }
      ],
      demoComponent: <CodeDemo />
    },
    {
      id: 'digital-transform',
      title: 'Digital Transformation Consulting',
      subtitle: 'Strategic Technology Leadership',
      icon: 'Zap',
      bgColor: 'bg-conversion-accent',
      description: 'Strategic consulting that bridges the gap between business objectives and technology implementation. We help enterprises navigate digital transformation with confidence.',
      technologies: ['Strategy', 'Architecture', 'Cloud', 'Analytics', 'Automation', 'Security'],
      timeline: '12-24 weeks',
      startingPrice: '$50,000',
      methodology: [
        { phase: 'Assessment', description: 'Current state analysis and gap identification' },
        { phase: 'Strategy', description: 'Digital roadmap and technology selection' },
        { phase: 'Implementation', description: 'Phased rollout with change management' },
        { phase: 'Optimization', description: 'Performance monitoring and continuous improvement' }
      ],
      metrics: [
        { value: '60%', label: 'Operational Efficiency Gain' },
        { value: '45%', label: 'Revenue Growth' },
        { value: '80%', label: 'Employee Satisfaction' }
      ]
    },
    {
      id: 'mvp-dev',
      title: 'MVP Development',
      subtitle: 'Rapid Prototyping & Market Validation',
      icon: 'Rocket',
      bgColor: 'bg-success',
      description: 'Fast-track your startup idea from concept to market with our proven MVP development process. Get to market quickly while maintaining quality and scalability.',
      technologies: ['React', 'Next.js', 'Firebase', 'Stripe', 'Vercel', 'Analytics'],
      timeline: '4-8 weeks',
      startingPrice: '$15,000',
      methodology: [
        { phase: 'Validation', description: 'Market research and feature prioritization' },
        { phase: 'Design', description: 'User experience and interface design' },
        { phase: 'Build', description: 'Rapid development with core features' },
        { phase: 'Launch', description: 'Market deployment and user feedback' }
      ],
      metrics: [
        { value: '70%', label: 'Faster Market Entry' },
        { value: '50%', label: 'Development Cost Savings' },
        { value: '85%', label: 'User Validation Rate' }
      ]
    },
    {
      id: 'enterprise-integration',
      title: 'Enterprise Integration',
      subtitle: 'Seamless System Connectivity',
      icon: 'Network',
      bgColor: 'bg-primary',
      description: 'Complex enterprise system integration that connects disparate systems, streamlines workflows, and enables data-driven decision making across your organization.',
      technologies: ['REST APIs', 'GraphQL', 'Microservices', 'Kafka', 'Redis', 'Kubernetes'],
      timeline: '16-32 weeks',
      startingPrice: '$75,000',
      methodology: [
        { phase: 'Mapping', description: 'System inventory and integration planning' },
        { phase: 'Architecture', description: 'Integration patterns and API design' },
        { phase: 'Development', description: 'Connector development and testing' },
        { phase: 'Deployment', description: 'Production rollout and monitoring' }
      ],
      metrics: [
        { value: '80%', label: 'Process Automation' },
        { value: '50%', label: 'Data Accuracy Improvement' },
        { value: '90%', label: 'System Reliability' }
      ]
    }
  ];

  useEffect(() => {
    document.title = 'Services - Manica Solutions';
  }, []);

  return (
    <div className="bg-background">
      <Helmet>
        <meta name="description" content="Comprehensive software development services including custom development, digital transformation, MVP development, and enterprise integration." />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand/10 via-secondary/5 to-accent/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gradient-brand mb-6">
              Our Services
            </h1>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
              Comprehensive software development solutions designed to accelerate your digital transformation 
              and drive measurable business outcomes.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center space-x-2 bg-card px-4 py-2 rounded-full border border-border">
                <Icon name="Code" size={16} />
                <span className="text-sm font-medium">Custom Development</span>
              </div>
              <div className="flex items-center space-x-2 bg-card px-4 py-2 rounded-full border border-border">
                <Icon name="Zap" size={16} />
                <span className="text-sm font-medium">Digital Transformation</span>
              </div>
              <div className="flex items-center space-x-2 bg-card px-4 py-2 rounded-full border border-border">
                <Icon name="Rocket" size={16} />
                <span className="text-sm font-medium">MVP Development</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Ecosystem Overview */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Service Ecosystem
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Our integrated approach combines multiple services to deliver comprehensive solutions
            </p>
          </div>
          
          <ServiceEcosystem 
            activeView={activeView}
            onViewChange={setActiveView}
          />
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Core Services
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Choose the service that best fits your current needs, or combine multiple services for a comprehensive solution
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                isExpanded={expandedService === service.id}
                onToggle={() => setExpandedService(expandedService === service.id ? null : service.id)}
                onCalculatorOpen={() => {
                  setSelectedCalculatorService(service.id);
                  setCalculatorOpen(true);
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline Section */}
      {expandedService && (
        <section className="py-16 bg-muted">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <ProcessTimeline 
              service={services.find(s => s.id === expandedService)}
            />
          </div>
        </section>
      )}

      {/* Success Metrics */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Proven Results Across Industries
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Our comprehensive approach delivers measurable outcomes that drive business growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'TrendingUp', value: '40%', label: 'Faster Time-to-Market', description: 'Average improvement for startup clients' },
              { icon: 'DollarSign', value: '60%', label: 'Cost Reduction', description: 'Operational savings for enterprises' },
              { icon: 'Users', value: '95%', label: 'Client Satisfaction', description: 'Based on project completion surveys' },
              { icon: 'Award', value: '99.9%', label: 'Uptime Delivered', description: 'Across all production systems' }
            ].map((metric, index) => (
              <div key={index} className="text-center p-6 bg-muted rounded-brand hover-lift">
                <div className="w-16 h-16 bg-secondary/10 rounded-brand flex items-center justify-center mx-auto mb-4">
                  <Icon name={metric.icon} size={28} className="text-secondary" />
                </div>
                <div className="text-3xl font-bold text-secondary mb-2">{metric.value}</div>
                <div className="text-lg font-semibold text-text-primary mb-2">{metric.label}</div>
                <div className="text-sm text-text-secondary">{metric.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Let's discuss how our services can accelerate your digital transformation journey
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              className="bg-white text-primary hover:bg-gray-100"
            >
              Schedule Strategy Call
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Calculator"
              iconPosition="left"
              onClick={() => setCalculatorOpen(true)}
              className="border-white text-white hover:bg-white/10"
            >
              Project Calculator
            </Button>
            <Link to="/contact-consultation">
              <Button
                variant="ghost"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                className="text-white hover:bg-white/10"
              >
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Project Calculator Modal */}
      <ProjectCalculator
        isOpen={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
        serviceId={selectedCalculatorService}
      />
    </div>
  );
};

export default ServicesDeepDive;